import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import Icon from './icon-map';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  animationDelay?: string;
}

/**
 * FeatureCard - Component for displaying individual features with animation
 * 
 * @param {string} icon - The name of the icon to display
 * @param {string} title - Feature title
 * @param {string} description - Feature description
 * @param {string} animationDelay - Optional delay class for animation sequencing
 */
const FeatureCard: React.FC<FeatureCardProps> = ({ 
  icon, 
  title, 
  description, 
  animationDelay = 'animate-fade-in'
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add(animationDelay);
            entry.target.classList.remove('opacity-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [animationDelay]);

  return (
    <div 
      ref={cardRef}
      className={cn(
        "bg-white p-6 rounded-xl border border-gray-100 shadow-sm",
        "hover:shadow-md transition-all duration-300 ease-in-out",
        "opacity-0"
      )}
    >
      <div className="mb-4 bg-purple-50 w-12 h-12 rounded-lg flex items-center justify-center">
        <Icon name={icon} className="h-6 w-6 text-purple-600" />
      </div>
      <h3 className="text-xl font-bold mb-2 text-gray-900">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

export default FeatureCard;
